/**
 * ExifUtils containing helping functions to manipulate exif data from Legacy LM.
 * NOTE: This version is slightly modified so it works outside of a web worker,
 *       as the original version is designed to run inside a web worker.
 */

export interface JpegHeaderData {
  segments: Array<Uint8ClampedArray>;
  exif: Array<{ [key: string]: any }>;
  exifSeg: number;
}
export class ExifUtils {
  public exifTagTypes: any;
  public ORIENTATION: any;
  constructor() {
    this.exifTagTypes = {
      // byte, 8-bit unsigned int:
      1: {
        getValue: function (
          dataView: { getUint8: (arg0: any) => any },
          dataOffset: any,
        ) {
          return dataView.getUint8(dataOffset);
        },
        size: 1,
      },
      // ascii, 8-bit byte:
      2: {
        getValue: function (
          dataView: { getUint8: (arg0: any) => number },
          dataOffset: any,
        ) {
          return String.fromCharCode(dataView.getUint8(dataOffset));
        },
        size: 1,
        ascii: true,
      },
      // short, 16 bit int:
      3: {
        getValue: function (
          dataView: { getUint16: (arg0: any, arg1: any) => any },
          dataOffset: any,
          littleEndian: any,
        ) {
          return dataView.getUint16(dataOffset, littleEndian);
        },
        size: 2,
      },
      // long, 32 bit int:
      4: {
        getValue: function (
          dataView: { getUint32: (arg0: any, arg1: any) => any },
          dataOffset: any,
          littleEndian: any,
        ) {
          return dataView.getUint32(dataOffset, littleEndian);
        },
        size: 4,
      },
      // rational = two long values, first is numerator, second is denominator:
      5: {
        getValue: function (
          dataView: { getUint32: (arg0: any, arg1: any) => number },
          dataOffset: number,
          littleEndian: any,
        ) {
          return (
            dataView.getUint32(dataOffset, littleEndian) /
            dataView.getUint32(dataOffset + 4, littleEndian)
          );
        },
        size: 8,
      },
      // slong, 32 bit signed int:
      9: {
        getValue: function (
          dataView: { getInt32: (arg0: any, arg1: any) => any },
          dataOffset: any,
          littleEndian: any,
        ) {
          return dataView.getInt32(dataOffset, littleEndian);
        },
        size: 4,
      },
      // srational, two slongs, first is numerator, second is denominator:
      10: {
        getValue: function (
          dataView: { getInt32: (arg0: any, arg1: any) => number },
          dataOffset: number,
          littleEndian: any,
        ) {
          return (
            dataView.getInt32(dataOffset, littleEndian) /
            dataView.getInt32(dataOffset + 4, littleEndian)
          );
        },
        size: 8,
      },
    };

    // undefined, 8-bit byte, value depending on field:
    this.exifTagTypes[7] = this.exifTagTypes[1];

    // Orientation possible values. These are always in relation to where row 0 and
    // column 0 are located in the image data.
    this.ORIENTATION = {
      NONE: 0, // Same as 1 in that there is nothing to rotate.
      TOP_LEFT: 1, // 1 = Horizontal (normal)
      TOP_RIGHT: 2, // 2 = Mirror horizontal
      BOTTOM_RIGHT: 3, // 3 = Rotate 180
      BOTTOM_LEFT: 4, // 4 = Mirror vertical
      LEFT_TOP: 5, // 5 = Mirror horizontal and rotate 270 CW
      RIGHT_TOP: 6, // 6 = Rotate 90 CW
      RIGHT_BOTTOM: 7, // 7 = Mirror horizontal and rotate 90 CW
      LEFT_BOTTOM: 8, // 8 = Rotate 270 CW
    };
  }

  /*
						Called to break the image header up into the various segments contained in it.

						@param {ArrayBuffer}    The raw jpeg file as an ArrayBuffer.
		*/
  getHeaderSegmentsFromFile = async (file: any) => {
    // Read in the first chunk of data.
    let rawImageArray = await this.readBytes(file, 0, 6);

    // Sanity check the JPG. We should always begin with a SOI (FFD8)
    if (
      rawImageArray.length === 0 ||
      (rawImageArray[0] !== 255 && rawImageArray[1] !== 216)
    ) {
      throw new Error(
        'Invalid JPG file detected. SOI bytes not found. Unable to parse JPG header.',
      );
    }

    // Determine the first chunk of data to read in.
    const firstSegLength = rawImageArray[4] * 256 + rawImageArray[5];

    // Now we can read our first chunk in and the while loop will move through the rest.
    // We need to read in the APP marker data plus the first 6 bytes we just read and then 4 bytes to
    // give us the next APP marker along with it's size.
    let currentStartByte = 0;
    let currentEndByte = firstSegLength + 6 + 4;
    rawImageArray = await this.readBytes(file, 0, currentEndByte);

    const segments = []; // Holds all the chunks that make up the full header data.
    let markerStart = 2; // Starts at 2 the first time as we want to skip the SOI marker.
    let endPoint;

    // We need to move past the start of image.
    currentStartByte += 2;

    // Begin looping through until we hit the start of the image.
    let seg;
    let length1;
    while (1) {
      // We hit a segment of data. Find out how big it is.
      length1 =
        rawImageArray[markerStart + 2] * 256 + rawImageArray[markerStart + 3];

      // End point is the APP marker bytes (the 2) plus the length of the data bytes.
      endPoint = markerStart + length1 + 2;

      // NOTE: IE11 doesn't support slice on typed arrays so just slice the buffer and create
      //       a new typed array with the output.
      seg = rawImageArray.subarray(markerStart, endPoint);
      segments.push(seg);

      // Read the next chunk in. The last 4 types of the current array are the next app marker.
      if (
        rawImageArray[markerStart] === 255 &&
        rawImageArray[markerStart + 1] === 218
      ) {
        // SOS - Start of compressed data hit! We're done
        break;
      } else {
        // Another APP marker. Keep going.
        currentStartByte = currentStartByte - markerStart + endPoint; // Move one byte to the start of the next
        // marker.
        currentEndByte =
          currentStartByte +
          rawImageArray[endPoint + 2] * 256 +
          rawImageArray[endPoint + 3];
        rawImageArray = await this.readBytes(
          file,
          currentStartByte,
          currentEndByte + 6,
        ); // Move past the next APP
        // marker as well as 2 bytes
        // for it's length.

        // Ensure marker start is always 0. Only should be something other than 0 the first time
        // through the loop due to SOI marker skipping.
        markerStart = 0;
      }
    }

    return segments;
  };

  /*
						Called to read a set of bytes from a given file.
		*/
  readBytes = async (
    file: any,
    start: string | number,
    end: string | number,
  ) => {
    // Sanity check the end byte we'll read to.
    if (end > file.size) {
      throw new Error(
        'readBytes called with end byte that would read past EOF.',
      );
    }

    let buffer;
    // Get the next slice.
    try {
      const blobSlice = file.slice(start, end);
      buffer = await blobSlice.arrayBuffer();
    } catch (e: any) {
      throw new Error(
        'Exception encountered attempting to read bytes from ' +
          file.name +
          '. start:' +
          start +
          ' end: ' +
          end +
          ' Error: ' +
          e.message,
      );
    }

    return new Uint8Array(buffer);
  };

  /*
						Called to break the image header up into the various segments contained in it.

						@param {Uint8Array}    The raw jpeg file as a typed array.
		*/
  getHeaderSegmentsFromBuffer = (rawImageArray: Uint8ClampedArray) => {
    let head = 0;
    const segments: Array<Uint8ClampedArray> = [];
    let length, endPoint, seg;

    // Sanity check the JPG. We should always begin with a SOI (FFD8)
    if (
      rawImageArray.length === 0 ||
      (rawImageArray[0] !== 255 && rawImageArray[1] !== 216)
    )
      throw new Error(
        'Invalid JPG file detected. SOI bytes not found. Unable to parse file.',
      );

    // Loop through until we get to the end of the image.
    while (1) {
      if (rawImageArray[head] === 255 && rawImageArray[head + 1] === 218) {
        // SOS - start of compressed data.
        // We hit the actual jpeg bits so no more header data to read.
        break;
      }
      if (rawImageArray[head] === 255 && rawImageArray[head + 1] === 216) {
        // SOI - start of image.
        //Advance 2 bytes to move passed marker.
        head += 2;
      } else {
        length = rawImageArray[head + 2] * 256 + rawImageArray[head + 3];
        endPoint = head + length + 2;

        // NOTE: IE11 doesn't support slice on typed arrays so just slice the buffer and create
        //       a new typed array with the output.
        seg = rawImageArray.subarray(head, endPoint);
        segments.push(seg);
        head = endPoint;
      }

      // Should always hit the compressed jpeg data before ever hitting this.
      if (head > rawImageArray.length) {
        break;
      }
    }

    return segments;
  };

  /*
		
		*/
  imageSizeFromSegments = (segments: string | any[]) => {
    let width;
    let height;

    // Start of Frame. 255 (FF) is the first byte. These are the possible second bytes.
    const SOF = [
      192, 193, 194, 195, 197, 198, 199, 201, 202, 203, 205, 206, 207,
    ];

    for (const seg of segments) {
      // If we find a SOF segment, get the width and height.
      if (SOF.indexOf(seg[1]) >= 0) {
        height = seg[5] * 256 + seg[6];
        width = seg[7] * 256 + seg[8];
        break;
      }
    }

    return { width: width, height: height };
  };

  /*
		
		*/
  getImageOrientationFromExifList = (exifList: string | any[]) => {
    let orientation = 1;

    let list = null;
    const numItems = exifList.length;
    for (let i = 0; i < numItems; i++) {
      // Get the next exif item.
      list = exifList[i].exif;

      if (null != list[274]) {
        orientation = list[274].value;
        break;
      }
    }

    return orientation;
  };

  /*
		
		*/
  setImageOrientation = (
    segments: { [x: string]: any },
    exif: string | any[],
    exifSegIdx: string | number,
    orientationValue: any,
  ) => {
    // Check if there was any thumbnail data.
    for (const directory of exif) {
      // This directory contain thumbnail info?
      if (null != directory.exif[274]) {
        // Has the thumbnail data. Lets remove it.
        const exifSeg = segments[exifSegIdx];

        // Get a data view and then update the directory count as we're removing two items.
        const dataView = new DataView(exifSeg.buffer);

        // Update the raw data in the byte array for orientation.
        dataView.setUint32(
          directory.exif[274].offset + 8,
          1,
          directory.exif[274].littleEndian,
        );

        // Update the EXIF since it's a separate data structure from the RAW data.
        directory.exif[274].value = orientationValue;

        break;
      }
    }
  };

  /*
						Called to check if an image needs to be rotated.
						
						param {Number} orientation    The orientation type to check.
		*/
  needsRotation = (orientation: number) => {
    let isRotated = false;
    if (
      orientation > this.ORIENTATION.TOP_LEFT &&
      orientation <= this.ORIENTATION.LEFT_BOTTOM
    )
      isRotated = true;

    return isRotated;
  };

  /*

	*/
  parseExifData = (
    dataView: { getUint32: any; byteLength: any; getUint16: any },
    offset: number | null,
  ) => {
    // If a subarray is passed then we need to know it's offset with the entire array buffer.
    if (null == offset) offset = 0;

    const tiffOffset = offset + 10;
    let littleEndian;
    let dirOffset;
    // Check for the ASCII code for "Exif" (0x45786966):
    if (dataView.getUint32(offset + 4) !== 0x45786966) {
      // No Exif data, might be XMP data instead
      return [];
    }
    if (tiffOffset + 8 > dataView.byteLength) {
      console.log('Invalid Exif data: Invalid segment size.');
      return [];
    }
    // Check for the two null bytes:
    if (dataView.getUint16(offset + 8) !== 0x0000) {
      console.log('Invalid Exif data: Missing byte alignment offset.');
      return [];
    }
    // Check the byte alignment:
    switch (dataView.getUint16(tiffOffset)) {
      case 0x4949:
        littleEndian = true;
        break;
      case 0x4d4d:
        littleEndian = false;
        break;
      default:
        console.log('Invalid Exif data: Invalid byte alignment marker.');
        return [];
    }
    // Check for the TIFF tag marker (0x002A):
    if (dataView.getUint16(tiffOffset + 2, littleEndian) !== 0x002a) {
      console.log('Invalid Exif data: Missing TIFF marker.');
      return [];
    }
    // Retrieve the directory offset bytes, usually 0x00000008 or 8 decimal:
    dirOffset = dataView.getUint32(tiffOffset + 4, littleEndian);
    const data: Array<{ [key: string]: any }> = [];
    while (dirOffset) {
      // Create a new directory object.
      const directoryData = { exif: {} };
      data.push(directoryData);

      // Parse the tags of the main image directory and retrieve the
      // offset to the next directory, usually the thumbnail directory:
      dirOffset = this.parseExifTags(
        dataView,
        tiffOffset,
        tiffOffset + dirOffset,
        littleEndian,
        directoryData,
      );
    }

    return data;
  };

  /*

		*/
  parseExifTags = (
    dataView: {
      byteLength: number;
      getUint16: (arg0: any, arg1: any) => any;
      getUint32: (arg0: any, arg1: any) => any;
    },
    tiffOffset: any,
    dirOffset: number,
    littleEndian: any,
    data: any,
  ) => {
    let tagsNumber, dirEndOffset, i;
    if (dirOffset + 6 > dataView.byteLength) {
      console.log('Invalid Exif data: Invalid directory offset.');
      return;
    }

    // Get the number of directory entries.
    // Each directory entry is 12 bytes long. It contains 4 parts
    // 1) tag number
    // 2) data format/type
    // 3) number of components (i.e. keywords has multiple where as dimension would have 1)
    // 4) the actual data
    // Typical example if EXIF is present:
    // [255, 225,   109, 209,    69, 120, 105, 102,   0, 0,         77, 77,         0, 42,     0, 0,  0, 8,      0, 12]
    //  ^ APP1 ^   ^APP1 Size^   ^ EXIF          ^    ^ 2 nulls ^   ^Engines^      ^TIFF^     ^offset to ^      ^ number of ^
    //             28113 bytes     if not is XMP        bytes      Tiff (II or MM)   marker    directory bytes    directories
    tagsNumber = dataView.getUint16(dirOffset, littleEndian); //  read in the two bytes for directory entries.
    dirEndOffset = dirOffset + 2 + 12 * tagsNumber;
    if (dirEndOffset + 4 > dataView.byteLength) {
      console.log('Invalid Exif data: Invalid directory size.');
      return;
    }

    // Store the start and end offsets of this directory.
    data.dirStartOffset = dirOffset;
    data.dirEndOffset = dirEndOffset;
    data.littleEndian = littleEndian;

    // Loop through and get all the tags in this directory.
    for (i = 0; i < tagsNumber; i += 1) {
      this.parseExifTag(
        dataView,
        tiffOffset,
        dirOffset + 2 + 12 * i, // tag offset
        littleEndian,
        data,
      );
    }

    // Return the offset to the next directory:
    return dataView.getUint32(dirEndOffset, littleEndian);
  };

  /*

		*/
  parseExifTag = (
    dataView: {
      getUint16: (arg0: any, arg1: any) => any;
      getUint32: (arg0: any, arg1: any) => any;
    },
    tiffOffset: any,
    offset: number,
    littleEndian: any,
    data: {
      exif: {
        [x: string]: {
          value: any;
          offset: any;
          type: any;
          length: any;
          littleEndian: any;
        };
      };
    },
  ) => {
    const tag = dataView.getUint16(offset, littleEndian);
    const type = dataView.getUint16(offset + 2, littleEndian);
    let length = dataView.getUint32(offset + 4, littleEndian);
    const tagValue = this.getExifValue(
      dataView,
      tiffOffset,
      offset,
      type, // tag type
      length, // tag length
      littleEndian,
    );

    data.exif[tag] = {
      value: tagValue,
      offset: offset,
      type: type,
      length: length,
      littleEndian: littleEndian,
    };
  };

  /*
						[1, 15,     0, 2,        0, 0, 0, 6,     0, 0, 0, 158, 1,    16,0, 2, 0, 0, 0, 14]
							^ Tag ^   ^ Tag ^        ^ Tag   ^
																			Type            Length
		*/
  getExifValue = (
    dataView: any,
    tiffOffset: any,
    offset: number,
    type: string | number,
    length: number,
    littleEndian: any,
  ) => {
    const tagType = this.exifTagTypes[type];
    let tagSize;
    let dataOffset;
    let values;
    let i;
    let str;
    let c;
    if (!tagType) {
      console.log('Invalid Exif data: Invalid tag type.');
      return;
    }
    tagSize = tagType.size * length;
    // Determine if the value is contained in the dataOffset bytes,
    // or if the value at the dataOffset is a pointer to the actual data:
    dataOffset =
      tagSize > 4
        ? tiffOffset + dataView.getUint32(offset + 8, littleEndian)
        : offset + 8;
    if (dataOffset + tagSize > dataView.byteLength) {
      console.log('Invalid Exif data: Invalid data offset.');
      return;
    }
    if (length === 1) {
      return tagType.getValue(dataView, dataOffset, littleEndian);
    }
    values = [];
    for (i = 0; i < length; i += 1) {
      values[i] = tagType.getValue(
        dataView,
        dataOffset + i * tagType.size,
        littleEndian,
      );
    }
    if (tagType.ascii) {
      str = '';
      // Concatenate the chars:
      for (i = 0; i < values.length; i += 1) {
        c = values[i];
        // Ignore the terminating NULL byte(s):
        if (c === '\u0000') {
          break;
        }
        str += c;
      }
      return str;
    }
    return values;
  };

  /*
		
		*/
  getThumbnailSegment = (
    segments: { [x: string]: any },
    exif: string | any[],
    exifSegIdx: string | number,
  ) => {
    let thumbInfo = null;

    // Check if there was any thumbnail data.
    for (const directory of exif) {
      // This directory contain thumbnail info?
      if (null != directory.exif[513]) {
        // Has the thumbnail data. Lets remove it.
        thumbInfo = {
          exifSeg: segments[exifSegIdx],
          directory: directory,
        };
        break;
      }
    }

    return thumbInfo;
  };

  /*
						Called to set the exif data back into the final jpeg. Will check if there is thumbnail
						markers and set the pointer to the data to 0 and it's length to 0 to fix an issue on the server.
		*/
  clearThumbnailData = (segments: any, exif: any, exifSegIdx: any) => {
    // Has the thumbnail data. Lets remove it.
    const thumbInfo = this.getThumbnailSegment(segments, exif, exifSegIdx);

    if (thumbInfo != null) {
      // Get a data view and then update the directory count as we're removing two items.
      const dataView = new DataView(thumbInfo.exifSeg.buffer);

      /*
						var str = new String(exifSeg.slice(directory.dirStartOffset, 4), "UTF-8"); // for UTF-8 encoding
						var numTags = dataView.getUint16(directory.dirStartOffset, directory.littleEndian);
						dataView.setUint16(directory.dirStartOffset, (numTags - 2), directory.littleEndian);
							THIS WORKS!!!To set them to 0
						*/
      dataView.setUint32(
        thumbInfo.directory.exif[513].offset + 8,
        0,
        thumbInfo.directory.exif[513].littleEndian,
      );
      dataView.setUint32(
        thumbInfo.directory.exif[514].offset + 8,
        0,
        thumbInfo.directory.exif[514].littleEndian,
      );

      // Remove the marks 513(thumb data offset) and 514(thumb data length)
      // TODO: 12 bytes to every marker. We could just nuke the markers.
      //exifSeg.splice(directory.exif[513].offset, 12);
      //exifSeg.splice(directory.exif[514].offset - 12, 12);
    }
  };

  /*
						Called to write JPEG header segments back into the resized image. All the segments except for the
						JIFF are written back (canvas creates a JIFF segment by default and is always the first one).
		*/
  insertExifIntoDataUri = (imageStr: string, segments: string | any[]) => {
    const buf = this.decode64(imageStr.replace('data:image/jpeg;base64,', ''));
    if (buf[2] !== 255 || buf[3] !== 224) {
      throw new Error("Couldn't find APP0 marker from resized image data.");
    }

    return this.insertExif(buf, segments);
  };

  /*
						Called to convert a jpeg image array into a dataUri.
		*/
  arrayToJpegDataUri = (imageArray: Uint8Array) => {
    // Finally add the image data.
    const jpegData: Array<string> = [];
    imageArray.forEach((item: number) => {
      jpegData.push(String.fromCharCode(item));
    });

    return 'data:image/jpeg;base64,' + btoa(jpegData.join(''));
  };

  /*

		*/
  insertExif = (array: Uint8Array, segments: string | any[]) => {
    let merged;
    const app0_length = array[4] * 256 + array[5];

    // Write how large this image is in bytes
    const numSegs = segments.length;

    // SOI (start of image) marker
    let newImage = new Uint8Array([255, 216]);
    for (let i = 0; i < numSegs; i++) {
      const segData = segments[i];

      if (
        (segData[0] === 255 && segData[1] === 237) ||
        (segData[0] === 255 && segData[1] === 225)
      ) {
        // IPTC (APP13 marker)
        // Add the IPTC segment
        merged = new Uint8Array(newImage.length + segData.length);
        merged.set(newImage);
        merged.set(segData, newImage.length);
        newImage = merged;
      }
    }
    // Get the JFIF/APP0 marker is from from the resized image.
    const jiff = new Uint8Array(array.buffer.slice(4 + app0_length));
    merged = new Uint8Array(newImage.length + jiff.length);
    merged.set(newImage);
    merged.set(jiff, newImage.length);
    newImage = merged;

    return newImage;
  };

  /*
						Called to decode a base64 encoded string.
		*/
  decode64 = (input: string) => {
    const binStr = atob(input);
    const buf = new Uint8Array(binStr.length);
    for (let p = 0; p < binStr.length; p++) {
      buf[p] = binStr.charCodeAt(p);
    }
    return buf;
  };
}
